import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Clock, CreditCard, CheckCircle } from 'lucide-react';
import { formatDate, formatCurrencyFull, formatHours, filterDetailRows } from '../utils/costingHelpers';

const StatusBadge = ({ status }) => {
    const s = status?.toLowerCase();
    if (s === 'billing') return <span className="mobile-status-badge mobile-status-billing"><CheckCircle size={12} /> Billing</span>;
    if (s === 'not billed yet') return <span className="mobile-status-badge mobile-status-pending"><Clock size={12} /> Pending</span>;
    if (s === 'credit') return <span className="mobile-status-badge mobile-status-credit"><CreditCard size={12} /> Credit</span>;
    return <span className="mobile-status-badge">{status || '-'}</span>;
};

const MobileDetailCards = ({
    data,
    summarySelection: leftSelection,
    detailSelection: rightSelection,
    onRowSelect,
}) => {
    const [sortBy, setSortBy] = useState('date');
    const [sortDir, setSortDir] = useState('desc');

    const filteredData = useMemo(() => filterDetailRows(data, leftSelection), [data, leftSelection]);

    const sortedData = useMemo(() => {
        const sorted = [...filteredData];
        sorted.sort((a, b) => {
            if (sortBy === 'date') {
                const da = new Date(a.jobDate || 0);
                const db = new Date(b.jobDate || 0);
                return sortDir === 'asc' ? da - db : db - da;
            }
            if (sortBy === 'cost') {
                const na = parseFloat(a.nett) || 0;
                const nb = parseFloat(b.nett) || 0;
                return sortDir === 'asc' ? na - nb : nb - na;
            }
            return 0;
        });
        return sorted;
    }, [filteredData, sortBy, sortDir]);

    const totals = useMemo(() => {
        return sortedData.reduce((acc, row) => ({
            hrs: acc.hrs + (parseFloat(row.hrs) || 0),
            nett: acc.nett + (parseFloat(row.nett) || 0),
        }), { hrs: 0, nett: 0 });
    }, [sortedData]);

    const toggleSort = (field) => {
        if (sortBy === field) {
            setSortDir(d => d === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(field);
            setSortDir('desc');
        }
    };

    if (!leftSelection) {
        return (
            <div className="mobile-empty-state">
                <p>Select a row from Summary to view details</p>
            </div>
        );
    }

    if (sortedData.length === 0) {
        return <div className="mobile-empty-state">No matching records</div>;
    }

    return (
        <div className="mobile-detail-cards">
            <div className="mobile-detail-header">
                <span className="mobile-detail-count">{sortedData.length} records</span>
                <div className="mobile-detail-sort-buttons">
                    <button
                        className={`mobile-sort-btn ${sortBy === 'date' ? 'active' : ''}`}
                        onClick={() => toggleSort('date')}
                    >
                        Date {sortBy === 'date' && (sortDir === 'asc' ? '↑' : '↓')}
                    </button>
                    <button
                        className={`mobile-sort-btn ${sortBy === 'cost' ? 'active' : ''}`}
                        onClick={() => toggleSort('cost')}
                    >
                        Cost {sortBy === 'cost' && (sortDir === 'asc' ? '↑' : '↓')}
                    </button>
                </div>
            </div>
            <div className="mobile-detail-list">
                {sortedData.map((row, idx) => {
                    const isSelected = rightSelection && rightSelection.lineId === row.lineId;
                    return (
                        <div
                            key={row.lineId || idx}
                            className={`mobile-detail-card ${isSelected ? 'mobile-detail-card-selected' : ''}`}
                            onClick={() => {
                                if (isSelected) {
                                    onRowSelect(null);
                                } else {
                                    onRowSelect({
                                        lineId: row.lineId,
                                        weekEnding: row.weekEnding,
                                        roleName: row.roleName,
                                        siteName: row.site_Name,
                                        jobDay: row.jobDay
                                    });
                                }
                            }}
                        >
                            <div className="mobile-detail-card-top">
                                <span className="mobile-detail-card-date">{formatDate(row.jobDate)}</span>
                                <span className="mobile-detail-card-day">{row.jobDay || '-'}</span>
                                <StatusBadge status={row.stat} />
                            </div>
                            <div className="mobile-detail-card-site">{row.site_Name || '-'}</div>
                            <div className="mobile-detail-card-provider">{row.providerName || '-'}</div>
                            <div className="mobile-detail-card-bottom">
                                <span className="mobile-detail-card-role">{row.roleName || '-'}</span>
                                <span className="mobile-detail-card-hours">{formatHours(row.hrs)} hrs</span>
                                <span className="mobile-detail-card-nett">{formatCurrencyFull(row.nett)}</span>
                            </div>
                            {row.reference && (
                                <div className="mobile-detail-card-ref">{row.reference}</div>
                            )}
                        </div>
                    );
                })}
            </div>
            <div className="mobile-detail-footer">
                <span className="mobile-detail-footer-label">Total</span>
                <span className="mobile-detail-footer-hours">{formatHours(totals.hrs)} hrs</span>
                <span className="mobile-detail-footer-nett">{formatCurrencyFull(totals.nett)}</span>
            </div>
        </div>
    );
};

MobileDetailCards.propTypes = {
    data: PropTypes.array,
    summarySelection: PropTypes.object,
    detailSelection: PropTypes.object,
    onRowSelect: PropTypes.func.isRequired,
};

export default MobileDetailCards;
